import type { LegacyParsedNode } from '@/msgdeskRuntime/legacyBridge/LegacyHtmlAstTypes'
import { LEGACY_SUB_CLASS_TO_FLAG } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { ItemSection, LegacyMoveDestination } from '../contracts/itemMovements'
import type { LegacyHandlerName } from '../contracts/legacyEvents'
import type { EditorFormModel } from '../contracts/editorModel'
import type { PooClientProjection } from '../../contracts/poo'
import type { EmailMessageMetadata } from '../../contracts/emailMessage'

export interface SubUiState {
  isVisible:          boolean;
  opacity:            number;
  isFav:              boolean;
  isFavIconOff:       boolean;
  isQrOn:             boolean;
  isHoverHighlighted: boolean;
  isFlagBlue:         boolean;
  answersOpacity:     number;
  submsgesOpacity:    number;
}

export function defaultSubUiState(): SubUiState {
  return {
    isVisible:          true,
    opacity:            1,
    isFav:              false,
    isFavIconOff:       false,
    isQrOn:             false,
    isHoverHighlighted: false,
    isFlagBlue:         false,
    answersOpacity:     1,
    submsgesOpacity:    1,
  };
}

export const SUB_CLASS_TO_FLAG = LEGACY_SUB_CLASS_TO_FLAG as Record<string, { flag: keyof SubUiState; addValue: boolean }>;

export function extractElIdFromHtmlId(htmlId: string): number | null {
  const info = htmlId.match(/^mcss-sub_info-cssm_\d+_(\d+)/);
  if (info) { const n = parseInt(info[1], 10); return isNaN(n) ? null : n; }
  const m = htmlId.match(/^mcss-(?:li_id|msg_icon|fav|qr|ul_id|sub_info)-cssm_(\d+)/);
  if (m) { const n = parseInt(m[1], 10); return isNaN(n) ? null : n; }
  const m2 = htmlId.match(/^mcss-sub_info_\d+-cssm_(\d+)/);
  if (m2) { const n = parseInt(m2[1], 10); return isNaN(n) ? null : n; }
  const m3 = htmlId.match(/^(?:mcss-list_txt-cssm_|time_)(\d+)$/);
  if (m3) { const n = parseInt(m3[1], 10); return isNaN(n) ? null : n; }
  const m4 = htmlId.match(/^mcss-(?:txtblock-cssm|qr_place-cssm_)(\d+)$/);
  if (m4) { const n = parseInt(m4[1], 10); return isNaN(n) ? null : n; }
  const m5 = htmlId.match(/^mcss-subs-cssm_[01]_(\d+)$/);
  if (m5) { const n = parseInt(m5[1], 10); return isNaN(n) ? null : n; }
  return null;
}

export interface SubsTextNodeData {
  kind: 'text';
  id: string;
  text: string;
}

export interface SubsElementNodeData {
  kind: 'element';
  id: string;
  tag: string;
  slot: string;
  attrs: Record<string, string>;
  childIds: string[];
  htmlId?: string;
}

export type SubsNodeData = SubsTextNodeData | SubsElementNodeData;

export interface TemplateDataState {
  text: Record<string, string>;
  attrs: Record<string, Record<string, string>>;
  elementToText: Record<string, string[]>;
  editorFormInputs?: Record<string, EditorFormInputState>;
  editorFormModel?: EditorFormModel;
  editorAttachments?: EditorAttachmentState[];
  newForm?: LegacyParsedNode[];
}

export interface EditorFormInputState {
  name: string;
  tag: string;
  type?: string;
  value?: string;
  checked?: boolean;
  multiple?: boolean;
  selectedValues?: string[];
}

export interface EditorAttachmentState {
  id?: string;
  src: string;
  filename?: string;
  progress?: string;
  removable: boolean;
  toggleable: boolean;
  active?: boolean;
  status?: 'idle' | 'uploading' | 'done' | 'error';
  error?: string;
  isUploadPlaceholder?: boolean;
  isAvatar?: boolean;
}

export interface MainAttachmentState {
  id: string;
  src: string;
  thumbSrc: string;
  filename?: string;
  title?: string;
  description?: string;
  width?: number;
  height?: number;
  size?: number;
  mediaKind?: 'audio' | 'image' | 'file';
  mime?: string;
  legacyIndex: number;
}

export interface ChromeElState {
  mounted: boolean;
  tagName?: string;
  attrs: Record<string, string>;
  visible?: boolean;
  classes: Record<string, boolean>;
  styles: Record<string, string | null>;
  props: Record<string, unknown>;
  text?: string;
  html?: string;
  value?: string;
}

export type SubsSection = ItemSection;
export type LegacySectionInput = SubsSection | 0 | 1;

export interface MoveCheckedOptions {
  section?: LegacySectionInput;
  mode?: string;
  reason?: string;
}

export interface SemanticMoveSyncMeta {
  toMode: LegacyMoveDestination;
  sourceMode?: string;
  section?: LegacySectionInput | 0 | 1;
  reason?: string;
  timestamp?: number;
}

export type ProjectionSyncResult = { section: SubsSection; page: number; ids: number[]; mode?: string };

export type LegacyLifecycleSnapshot = {
  commandDepth: number;
  pendingAjax: number;
  activeAjaxCallbacks: number;
  queueDepth: number;
  projectionSeq: number;
  traceDepth: number;
  legacyBusy: boolean;
  lastReason?: string;
  lastSettledAt?: number;
};

export type LegacyLifecycleStateListener = (snapshot: LegacyLifecycleSnapshot) => void;

export type LegacyIdleWaiter = {
  resolve: () => void;
};

export type LegacyBusyTransitionKind =
  | 'busy:global-acquire'
  | 'busy:global-release'
  | 'busy:global-block'
  | 'busy:object-acquire'
  | 'busy:object-release'
  | 'busy:object-block';

export type LegacyLifecycleTraceEntry = {
  id: number;
  parentId?: number;
  type: 'command:start' | 'command:end' | 'ajax:start' | 'ajax:end' | 'ajax:success:start' | 'ajax:success:end' | 'projection' | LegacyBusyTransitionKind;
  name: string;
  timestamp: number;
  depth: number;
  details?: unknown;
  counters: {
    commandDepth: number;
    pendingAjax: number;
    activeAjaxCallbacks: number;
    legacyBusy: boolean;
  };
};

/**
 * Live paging facts for one root section, projected from the legacy paging
 * model (page meta + cumulative page counter + tree count) after every paging
 * sync. Absent before runtime boot — selectors fall back to boot-count
 * arithmetic so SSR and the first client render stay identical.
 */
export interface SectionPagingSummary {
  currentPage: number;
  /** Legacy pages_data meta last-page number when known, else derived. */
  totalPages: number;
  /** Live tree count for the section in the projected mode. */
  total: number;
  /** Cumulative rows shown through the current page (legacy page counter). */
  shownThrough: number;
  /** Legacy _set_more_info rule: total - shownThrough, floored at 0. */
  moreCount: number;
  pageSize: number;
}

export interface SubsRenderProjection {
  rootIds: Record<SubsSection, number[]>;
  currentPage: Partial<Record<SubsSection, number>>;
  /** Per-section live paging summary for the CURRENT projection mode. */
  paging?: Partial<Record<SubsSection, SectionPagingSummary>>;
  mode: string;
  activeSection: SubsSection;
}

export type SubRowVariant =
  | 'legacy'
  | 'auto'
  /** Neutral shared folder-row look (favs/drafts/trash pages). */
  | 'folder'
  | 'compact'
  | 'comments'
  | 'email'
  | 'card'
  | 'chat'
  | 'business'
  | 'dense'
  | 'cards'
  | 'contacts'
  | 'facebook'
  | 'flickr'
  | 'fullscreen'
  | 'gallery'
  | 'gmail'
  | 'hackernews'
  | 'legacystd'
  | 'hotmail'
  | 'instagram'
  | 'notes'
  | 'phpbb'
  | 'presentation'
  | 'tiles'
  | 'twitter'
  | 'wiki';
export type SubVisualVariant = Exclude<SubRowVariant, 'legacy' | 'auto'>;

export interface SubAttachmentState {
  id: string | number;
  src: string;
  thumbSrc: string;
  storedName?: string;
  basePath?: string;
  filename: string;
  title?: string;
  description?: string;
  width?: number;
  height?: number;
  size?: number;
  storage?: number;
  mediaKind?: 'audio' | 'image' | 'file';
  mime?: string;
}

export interface SubRenderState {
  elId: number;
  title: string;
  /** legacy compact link text: name key for messages, subject for answers
   * (the payload's namekey_subject field, doc 32) */
  namekeySubject?: string;
  text: string;
  html?: string;
  datetime?: string | number;
  author?: string;
  avatarPath?: string;
  attachments?: SubAttachmentState[];
  access?: Record<string, unknown>;
  permissions?: Record<string, unknown>;
  /** BLOCKED-ACCESS-MODEL: the typed semantic grant emitted by msgdataApi on
   * grant-aware requests (facade.access_grant). Preferred over the
   * bug-compatible access/permissions namespaces when present. */
  accessGrant?: MsgAccessGrant;
  email?: Record<string, unknown>;
  display?: Record<string, unknown>;
  href?: string;
  codeKey?: string;
  msgId?: string | number;
  subNumber?: string | number;
  msgType?: string;
  viewType?: string | number;
  isMsg?: boolean;
  variant?: SubVisualVariant;
  flags: {
    isVisible: boolean;
    isChecked: boolean;
    isFavorite: boolean;
    isQuickReplyOpen: boolean;
    isHoverHighlighted: boolean;
  };
  counts: {
    answers: number;
    submsges: number;
  };
  children: Record<SubsSection, number[]>;
  legacy: {
    data?: any;
    rootNodeIds: string[];
    rootSlot?: string;
  };
}

export interface SubsLoadingState {
  mode: string;
  count: number;
  elId: number;
}

export interface SubsStoreState {
  nodesById: Record<string, SubsNodeData>;
  nodesByElId: Record<number, string[]>;
  htmlIdToNodeId: Record<string, string>;
  childrenMap: Record<number, { answers: number[]; submsges: number[] }>;
  activeIds: number[];
  syncVersion: number;
  visibilityMap: Record<number, boolean>;
  subStates: Record<number, SubUiState>;
  nextNodeSeq: number;
  templateData: TemplateDataState;
  legacyData: LegacyDataState;
  renderProjection: SubsRenderProjection;
  /** SSR/pre-runtime roots for collections intentionally shown beside the
   * active projection (Notes root shows real Favorites as Pinned). */
  auxiliaryRootIds?: Record<string, Partial<Record<SubsSection, number[]>>>;
  chromeByContract: Record<string, ChromeElState>;
  subsLoading: SubsLoadingState | null;
  /** Per-row nested-children fold: [answersHidden, submsgesHidden]. React
   * mirror of the legacy tree showed-counter — reconciled by the
   * subsDisclosure pipeline after toggle-children (the legacy fadeToggle on
   * the React-owned feed ul cannot represent it). */
  subChildrenHidden: Record<number, [boolean, boolean]>;
  pooProtocol?: PooClientProjection;
}

export interface LegacyDataState {
  msgesJson?: string;
  initJson?: string;
  msges?: any;
  init?: any;
  phpModel?: any;
  phpSubsModels?: Partial<Record<SubsSection, {
    payload: any;
    reason: string;
    updatedAt: number;
  }>>;
  containers?: Partial<Record<LegacyDataContainerKind, LegacyDataContainerState>>;
  runtime?: {
    msgData?: any;
    msgInputs?: Record<string, unknown>;
    reason: string;
    syncedAt: number;
  };
}


export type LegacyDataContainerKind = 'init' | 'msges' | 'msgesDataJson';

export interface LegacyDataContainerState {
  id: 'mcss-init_data-cssm' | 'mcss-msges_data-cssm' | 'mcss-msges_data_json-cssm';
  rawText: string;
  parsed?: any;
  consumed: boolean;
  reason: string;
  updatedAt: number;
  consumedAt?: number;
}

export interface MsgObjMainState {
  source: 'legacy-runtime' | 'hidden-json' | 'php-model' | 'templateData';
  subject?: string;
  body?: string;
  bodyHtml?: string;
  title?: string;
  datetime?: string | number;
  author?: string;
  codeKey?: string;
  nameKey?: string;
  avatarPath?: string;
  msgId?: string | number;
  msgType?: string;
  isHtml?: boolean;
  isMsg?: boolean;
  isLocked?: boolean;
  email?: {
    to: unknown;
    from: unknown;
    fromDisplay: string;
    message: EmailMessageMetadata | null;
  };
  isEmpty?: {
    subject: boolean;
    body: boolean;
  };
  counts?: Record<string, unknown>;
  flags?: Record<string, unknown>;
  inputs?: Record<string, unknown>;
  defaults?: Record<string, unknown>;
  legacy?: {
    msgData?: any;
    initData?: any;
  };
  fields: Record<string, unknown>;
  templateData: TemplateDataState;
}

export interface MsgAccessState {
  showUnlocked: boolean;
  editUnlocked: boolean;
  forbidAnswers: boolean;
  forbidSubmsges: boolean;
}

// BLOCKED-ACCESS-MODEL (msgdataApi/msg/access.ts is the emitting owner; this
// mirrors its wire shape 1:1 — snake_case keys are the payload contract).
export type MsgAccessReason = 'none' | 'show-pin' | 'edit-pin' | 'time' | 'parent' | 'members';
export type MsgAccessChallenge =
  | 'none'
  | 'inline-show-pin'
  | 'inline-edit-pin'
  | 'time-info'
  | 'login'
  | 'join-members';
export type MsgMembersStatus = 'off' | 'member' | 'pending' | 'none';

export interface MsgAccessGrant {
  reason: MsgAccessReason;
  challenge: MsgAccessChallenge;
  capabilities: {
    can_view_content: boolean;
    can_reply: boolean;
    can_create_named_message: boolean;
    can_edit: boolean;
    can_use_drafts_trash: boolean;
    can_check_or_move: boolean;
    can_see_parents: boolean;
  };
  viewer: {
    is_logged: boolean;
    is_owner: boolean;
    msg_id: string;
    code_key: string;
    name_key: string;
    members: MsgMembersStatus;
  };
  /** Only when reason==='time': which side of the window the viewer is on. */
  time_side?: 'early' | 'elapsed';
  members_inbox?: { msg_id: string };
  members_root?: { msg_id: string; code_key: string };
}

// PARENTS-MENU: one typed ordered projection of the serialized parents chain
// (msgdataApi msgParents shape:
// {number: [code_key, label, msg_type, is_msg, subject]}, numbers counting
// down toward the root; the boot payload carries it grants=1-gated).
export interface ParentLink {
  /** Route key: a parent desk is `/${codeKey}`. */
  codeKey: string;
  /** Display label — the parent name key, or the code key when unnamed. */
  label: string;
  /** Legacy chain position (the current message is max(number)+1). */
  number: number;
  /** Persisted product type from the already-loaded parent MsgObj. */
  msgType: string;
  /** True for a named-message parent; false for an answer parent. */
  isMsg: boolean;
  /** Parent subject when present. The ordinary menu still uses `label`. */
  subject: string;
}

export interface ParentsMenuState {
  /** False when hidden by is_hide_parents or a show lock. */
  canSee: boolean;
  /** Authoritative parent count (count_subs_obj.parents sum). */
  count: number;
  /** Nearest ancestor first — the legacy _html_parents_ul render order. */
  links: ParentLink[];
}

export interface MsgPageSettingsState {
  foldAnswers: boolean;
  foldSubmsges: boolean;
  viewCountAnswersFirstPage: number;
  viewCountSubmsgesFirstPage: number;
  viewCountMenuTabs: number;
}

export interface MsgObjEditorState {
  inputs: Record<string, unknown>;
  formInputs?: Record<string, EditorFormInputState>;
  formModel?: EditorFormModel;
  templateData: TemplateDataState;
}

export interface MsgObjTemplateState {
  data: TemplateDataState;
}

export interface MsgObjState {
  syncVersion: number;
  subs: SubsStoreState;
  main: MsgObjMainState;
  editor: MsgObjEditorState;
  template: MsgObjTemplateState;
}

export type MsgObjCommandTarget =
  | { kind: 'sub'; elId: number }
  | { kind: 'subFlag'; elId: number }
  | { kind: 'favoriteIcon'; elId: number }
  | { kind: 'quickReplyIcon'; elId: number }
  | { kind: 'subAnswersList'; elId: number }
  | { kind: 'subMessagesList'; elId: number }
  | { kind: 'input'; htmlId: string }
  | { kind: 'html'; htmlId: string }
  | { kind: 'paging'; section: SubsSection; page?: number };

export type MsgObjSemanticCommand =
  | { type: 'setActive'; target: MsgObjCommandTarget; active: boolean }
  | { type: 'setChecked'; target: MsgObjCommandTarget; checked: boolean }
  | { type: 'checkSub'; el: unknown; elId: number; checked: boolean }
  | { type: 'checkVisibleSubs'; el: unknown; section: LegacySectionInput }
  | { type: 'moveCheckedTo'; el: unknown; section: LegacySectionInput; toMode: LegacyMoveDestination; sourceMode?: string; reason?: string }
  | { type: 'setVisible'; target: MsgObjCommandTarget; visible: boolean }
  | { type: 'setCurrentPage'; section: SubsSection; page: number }
  | { type: 'showArray'; section: SubsSection; ids: number[] }
  | { type: 'delete'; target: MsgObjCommandTarget }
  | { type: 'legacyEvent'; name: LegacyHandlerName; args: unknown[] };
